/**
 * WebSocket kliens a szoba-szerverhez. Nincs benne játéklogika: üzeneteket
 * küld/fogad, és eseményként továbbadja őket a lobbynak / a verseny-szinkronnak.
 */
import type { InputState } from '../core/types';
import type {
  ClientMessage,
  InputWire,
  OnlineMatchInfo,
  OnlineRoomView,
  OnlineSettings,
  ServerMessageMap,
  Snapshot,
} from './protocol';

type Handler<K extends keyof ServerMessageMap> = (msg: ServerMessageMap[K]) => void;

const CONNECT_TIMEOUT = 8000;
const PING_INTERVAL = 2500;
const SERVER_EVENTS = new Set<string>([
  'room',
  'start',
  'input',
  'snapshot',
  'events',
  'pause',
  'result',
  'left',
  'closed',
  'left-room',
  'error',
]);

export class OnlineClient {
  /** mért kör-útidő (ms), simítva */
  rtt = 0;
  /** az utolsó ismert szobanézet (a lobby ebből tud visszatérni) */
  room: OnlineRoomView | null = null;
  /** forgalmi számlálók (diagnosztika): JSON-karakterek, ~byte-ban */
  bytesIn = 0;
  bytesOut = 0;
  snapshotsIn = 0;
  snapshotsOut = 0;
  snapshotBytesIn = 0;
  snapshotBytesOut = 0;
  private socket: WebSocket | null = null;
  private readonly handlers = new Map<keyof ServerMessageMap, Set<(msg: never) => void>>();
  private pingTimer = 0;
  private closedByUser = false;
  private openResolve: (() => void) | null = null;
  private openReject: ((error: Error) => void) | null = null;

  constructor(readonly url = OnlineClient.defaultUrl()) {}

  static defaultUrl(): string {
    const explicit = new URLSearchParams(window.location.search).get('ws');
    if (explicit) return explicit;
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${scheme}//${window.location.host}/ws`;
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(): Promise<void> {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      let socket: WebSocket;
      try {
        socket = new WebSocket(this.url);
      } catch (error) {
        reject(new Error(`Nem sikerült csatlakozni: ${(error as Error).message}`));
        return;
      }
      this.socket = socket;
      socket.binaryType = 'arraybuffer';

      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        this.openReject = null;
        try {
          socket.close();
        } catch {
          /* ignore */
        }
        reject(new Error('A szoba-szerver nem válaszol. Fut a `npm run server`?'));
      }, CONNECT_TIMEOUT);

      this.openResolve = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        this.openResolve = null;
        this.openReject = null;
        this.startPing();
        resolve();
      };
      this.openReject = (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        this.openResolve = null;
        this.openReject = null;
        reject(error);
      };

      socket.onopen = () => this.openResolve?.();
      socket.onerror = () => {
        if (!this.connected) this.openReject?.(new Error('Nem sikerült elérni a szoba-szervert.'));
      };
      socket.onclose = (event) => {
        window.clearInterval(this.pingTimer);
        this.pingTimer = 0;
        this.room = null;
        this.openReject?.(new Error('A kapcsolat megszakadt a szoba-szerverrel.'));
        if (!this.closedByUser) {
          this.emit('closed', {
            t: 'closed',
            reason: 'socket-closed',
            message: event.reason || 'Megszakadt a kapcsolat a szerverrel.',
          });
        }
        this.socket = null;
      };
      socket.onmessage = (event) => this.onRawMessage(event.data);
    });
  }

  on<K extends keyof ServerMessageMap>(type: K, handler: Handler<K>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as (msg: never) => void);
    return () => set?.delete(handler as (msg: never) => void);
  }

  send(message: ClientMessage): void {
    if (!this.connected) return;
    let text: string;
    try {
      text = JSON.stringify(message);
    } catch {
      return;
    }
    const bytes = text.length;
    this.bytesOut += bytes;
    if (message.t === 'snapshot') {
      this.snapshotsOut += 1;
      this.snapshotBytesOut += bytes;
    }
    try {
      this.socket?.send(text);
    } catch {
      /* a close handler majd jelez */
    }
  }

  create(name: string, settings: Partial<OnlineSettings>): void {
    this.send({ t: 'create', name, settings });
  }

  join(code: string, name: string): void {
    this.send({ t: 'join', code, name });
  }

  pick(character: string): void {
    this.send({ t: 'pick', character });
  }

  updateSettings(settings: Partial<OnlineSettings>): void {
    this.send({ t: 'settings', settings });
  }

  start(picks: string[]): void {
    this.send({ t: 'start', picks });
  }

  sendInput(input: InputState): void {
    const wire: InputWire = {
      s: round(input.steer, 3),
      t: round(input.throttle, 3),
      b: round(input.brake, 3),
      d: input.drift,
      u: input.useItem,
      a: input.ability,
    };
    this.send({ t: 'input', input: wire });
  }

  sendSnapshot(snapshot: Snapshot): void {
    this.send({ t: 'snapshot', s: snapshot });
  }

  sendEvents(events: Snapshot['ev']): void {
    if (events.length === 0) return;
    this.send({ t: 'events', e: events });
  }

  /** A gazda jelzi, ha a fül háttérbe kerül (a szimuláció megáll). */
  sendPause(paused: boolean): void {
    this.send({ t: 'pause', paused });
  }

  raceEnd(result: unknown): void {
    this.send({ t: 'raceEnd', result });
  }

  toLobby(): void {
    this.send({ t: 'toLobby' });
  }

  leave(): void {
    if (this.connected) this.send({ t: 'leave' });
    this.close();
  }

  close(): void {
    this.closedByUser = true;
    window.clearInterval(this.pingTimer);
    this.pingTimer = 0;
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
    try {
      socket.close(1000, 'viszlát');
    } catch {
      /* ignore */
    }
  }

  /** A lobby a `start` üzenetből kapja a teljes meccs-leírást. */
  private onRawMessage(data: unknown): void {
    if (typeof data !== 'string') return;
    this.bytesIn += data.length;
    let msg: { t?: string };
    try {
      msg = JSON.parse(data) as { t?: string };
    } catch {
      return;
    }
    if (!msg || typeof msg.t !== 'string') return;
    if (msg.t === 'snapshot') {
      this.snapshotsIn += 1;
      this.snapshotBytesIn += data.length;
    }
    if (msg.t === 'pong') {
      const pong = msg as unknown as ServerMessageMap['pong'];
      const sample = performance.now() - pong.ts;
      if (Number.isFinite(sample) && sample >= 0) {
        this.rtt = this.rtt === 0 ? sample : this.rtt * 0.75 + sample * 0.25;
      }
      return;
    }
    if (!SERVER_EVENTS.has(msg.t)) return;
    if (msg.t === 'room') this.room = (msg as ServerMessageMap['room']).room;
    else if (msg.t === 'closed' || msg.t === 'left-room') this.room = null;
    this.emit(msg.t as keyof ServerMessageMap, msg as never);
  }

  private emit<K extends keyof ServerMessageMap>(type: K, msg: ServerMessageMap[K]): void {
    const set = this.handlers.get(type);
    if (!set) return;
    for (const handler of [...set]) {
      try {
        (handler as unknown as (value: ServerMessageMap[K]) => void)(msg);
      } catch (error) {
        console.error('[online] eseménykezelő hiba:', error);
      }
    }
  }

  private startPing(): void {
    window.clearInterval(this.pingTimer);
    this.pingTimer = window.setInterval(() => {
      if (!this.connected) return;
      this.send({ t: 'ping', ts: performance.now() });
    }, PING_INTERVAL);
  }
}

const round = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export type { OnlineMatchInfo, Snapshot };
